import React, {useMemo} from 'react'
import {Box, useRecipe} from '@chakra-ui/react3'
import {Global} from '@emotion/react'
import messageBarRecipe from './index.recipe'
import CMSContent from '../../../components/cms-content'
import useCmsContent, {
    TYPE_CONTENT_SLOT
} from '../../../components/cms-content/hooks/use-cms-content'
import prepareHtmlForInjection from '../../../components/cms-content/util/prepare-html-for-injection'

/**
 * Promotional message bar rendered as a static, full-width element at the very
 * top of the page — above the header and in normal document flow, so it scrolls
 * away as the user scrolls down.
 *
 * Renders the `message-bar` CMS content slot and keeps its content full width. Renders nothing
 * when the slot has no CMS modules. While present it publishes
 * `--header-message-bar-block-size`, the message-bar term of the header height
 * chain in `app/leap/theme/global-css.js`.
 *
 * @component
 * @returns {JSX.Element|null} The message bar, or `null` when the slot is empty.
 */
const MessageBar = () => {
    const recipe = useRecipe({recipe: messageBarRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {data: messageBar} = useCmsContent('message-bar', TYPE_CONTENT_SLOT)
    const {hasCmsModules} = useMemo(
        () => prepareHtmlForInjection(messageBar?.c_body),
        [messageBar?.c_body]
    )

    if (!hasCmsModules) {
        return null
    }

    return (
        <Box css={styles}>
            <Global
                styles={{
                    ':root': {
                        '--header-message-bar-block-size': 'var(--cms-message-bar-block-size)'
                    }
                }}
            />
            <CMSContent
                id="message-bar"
                type={TYPE_CONTENT_SLOT}
                content={messageBar}
                showError={false}
            />
        </Box>
    )
}

export default MessageBar
